import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot
} from '../../components/ui/input-otp';
import { Phone, ArrowRight, Leaf, ShieldCheck } from '@phosphor-icons/react';
import { MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, user } = useAuth();
  const [step, setStep] = useState('phone');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpMethod, setOtpMethod] = useState('whatsapp');
  const [otpConfig, setOtpConfig] = useState({ sms_otp_enabled: false, whatsapp_otp_enabled: true });

  useEffect(() => {
    if (isAuthenticated && user) {
      if (!user.profile_complete) navigate('/complete-profile', { replace: true });
      else if (user.kyc_status === 'pending' && user.profile_complete) navigate('/kyc-upload', { replace: true });
      else navigate('/', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/otp-config`);
        setOtpConfig(res.data);
        if (res.data.whatsapp_otp_enabled && !res.data.sms_otp_enabled) setOtpMethod('whatsapp');
      } catch {}
    })();
  }, []);

  const handleSendOtp = async () => {
    if (mobile.length !== 10) { toast.error('Enter valid 10-digit mobile'); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/send-otp`, { mobile, otp_method: otpMethod });
      setStep('otp');
      toast.success(`OTP sent via ${otpMethod === 'whatsapp' ? 'WhatsApp' : 'SMS'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    }
    setLoading(false);
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) { toast.error('Enter complete OTP'); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { mobile, otp });
      login(response.data.token, response.data.user);
      if (response.data.is_new_user || !response.data.user.profile_complete) navigate('/complete-profile', { replace: true });
      else if (response.data.user.kyc_status === 'pending') navigate('/kyc-upload', { replace: true });
      else navigate('/', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    }
    setLoading(false);
  };

  const bothEnabled = otpConfig.sms_otp_enabled && otpConfig.whatsapp_otp_enabled;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="absolute inset-0 h-64 bg-cover bg-center"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1679231613212-b0ffa203e490?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxhZ3JpY3VsdHVyZSUyMG1vZGVybiUyMGZhcm0lMjB0cmFjdG9yJTIwbWluaW1hbHxlbnwwfHx8fDE3NzUyNDI3MzB8MA&ixlib=rb-4.1.0&q=85')` }}>
        <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/80 to-white" />
      </div>

      <div className="relative flex-1 flex flex-col items-center justify-center px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div>
            <h1 className="text-2xl font-bold font-outfit text-neutral-900">Avantra Chemicals</h1>
            <p className="text-sm text-neutral-500">B2B Agri Input Platform</p>
          </div>
        </div>

        <div className="w-full max-w-md bg-white border border-neutral-200 rounded-xl p-6 md:p-8">
          {step === 'phone' ? (
            <>
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold font-outfit text-neutral-900">Welcome Back</h2>
                <p className="text-neutral-500 mt-1">Enter your mobile number to continue</p>
              </div>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="mobile" className="text-neutral-700">Mobile Number</Label>
                  <div className="relative mt-1.5">
                    <Phone size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input id="mobile" type="tel" placeholder="Enter 10-digit mobile number"
                      value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="pl-10 h-12 text-lg" data-testid="mobile-input" />
                  </div>
                </div>
                {bothEnabled && (
                  <div>
                    <Label className="text-neutral-700 mb-2 block">Send OTP via</Label>
                    <div className="grid grid-cols-2 gap-2" data-testid="otp-method-selector">
                      <button type="button" onClick={() => setOtpMethod('sms')}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium ${otpMethod === 'sms' ? 'border-red-600 bg-red-50 text-red-700' : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'}`}
                        data-testid="otp-method-sms">
                        <MessageSquare className="w-4 h-4" /> SMS OTP
                      </button>
                      <button type="button" onClick={() => setOtpMethod('whatsapp')}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all text-sm font-medium ${otpMethod === 'whatsapp' ? 'border-green-600 bg-green-50 text-green-700' : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300'}`}
                        data-testid="otp-method-whatsapp">
                        <MessageSquare className="w-4 h-4" /> WhatsApp OTP
                      </button>
                    </div>
                  </div>
                )}
                {!bothEnabled && otpConfig.whatsapp_otp_enabled && (
                  <p className="text-xs text-green-600 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> OTP will be sent via WhatsApp</p>
                )}
                <Button onClick={handleSendOtp} disabled={loading || mobile.length !== 10}
                  className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-medium" data-testid="send-otp-button">
                  {loading ? 'Sending...' : 'Get OTP'} <ArrowRight size={20} className="ml-2" />
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck size={28} className="text-red-600" />
                </div>
                <h2 className="text-xl font-semibold font-outfit text-neutral-900">Verify OTP</h2>
                <p className="text-neutral-500 mt-1">
                  Enter the 6-digit code sent to +91 {mobile}
                  {otpMethod === 'whatsapp' && <span className="block text-xs text-green-600 mt-1">via WhatsApp</span>}
                </p>
              </div>
              <div className="space-y-4">
                <div className="flex justify-center">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                    <InputOTPGroup>
                      <InputOTPSlot index={0} /><InputOTPSlot index={1} /><InputOTPSlot index={2} />
                      <InputOTPSlot index={3} /><InputOTPSlot index={4} /><InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <Button onClick={handleVerifyOtp} disabled={loading || otp.length !== 6}
                  className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-medium" data-testid="verify-otp-button">
                  {loading ? 'Verifying...' : 'Verify & Continue'}
                </Button>
                <button onClick={() => { setStep('phone'); setOtp(''); }}
                  className="w-full text-center text-sm text-neutral-500 hover:text-red-600 transition-colors" data-testid="change-number-button">
                  Change mobile number
                </button>
              </div>
            </>
          )}
        </div>
        <p className="text-xs text-neutral-400 mt-8 text-center">By continuing, you agree to our Terms of Service and Privacy Policy</p>
      </div>
    </div>
  );
};

export default LoginPage;
